import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../Contexts/LanguageContext';
import './Inspection.scss';
import ReactGA from 'react-ga';
import TagManager from 'react-gtm-module';

const Inspection = () => {
  const { getLanguageData, language } = useLanguage();
  const data = getLanguageData();

  const sectionTitle = data.inspectionSection.heading;
  const relatedVideos = data.inspectionSection.relatedVideos;
  const downloadManual = data.inspectionSection.downloadManual;
  const relatedVideosTitle = data.inspectionSection.relatedVideosTitle;

   const getPoster = (poster) => {
    const fallback = `${process.env.PUBLIC_URL || ''}/Assets/images/poster.png`;
    if (!poster) return fallback;
    if (poster.startsWith('http') || poster.startsWith('/')) return poster;
    return `${process.env.PUBLIC_URL || ''}/Assets/videos/${poster}`;
  };

  const [selectedVideo, setSelectedVideo] = useState(relatedVideos[0]);
  const [videoDurations, setVideoDurations] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);

  // Function to handle video click
  const handleVideoClick = (video, index) => {
    ReactGA.event({
      category: 'Video',
      action: 'Click',
      label: `Video ${index + 1}`,
    });

    const GTM_ID = "TRACKING_ID";
    TagManager.initialize({ gtmId: GTM_ID });

    setSelectedVideo(video);
    setActiveIndex(index);
    
    if (videoDurations[video.video]) {
      return;
    }

    const tempVideo = document.createElement('video');
    tempVideo.src = `/Assets/videos/${video.video}`;
    tempVideo.addEventListener('loadedmetadata', () => {
      setVideoDurations((prevDurations) => ({
        ...prevDurations,
        [video.video]: tempVideo.duration,
      }));
    });
  };

  // Function to handle loaded metadata
  const handleLoadedMetadata = (event) => {
    const videoId = event.target.id;
    setVideoDurations((prevDurations) => ({
      ...prevDurations,
      [videoId]: event.target.duration,
    }));
  };

  // Function to handle play/pause
  const handlePlayPause = () => {
    const video = previewVideoRef.current;
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const [isPlaying, setIsPlaying] = useState(true);
  const [isAutoplaying, setIsAutoplaying] = useState(true);
  const previewVideoRef = useRef(null);

  useEffect(() => {
    const video = previewVideoRef.current;
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    const handleAutoplayEnd = () => setIsAutoplaying(false);
    video.addEventListener('ended', handleAutoplayEnd);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleAutoplayEnd);
    };
  }, []);

  useEffect(() => {
    // Find the selected video by its video property
    const foundVideo = relatedVideos.find(video => video.video === selectedVideo.video);
    // If the video is found, update selectedVideo to keep track of it
    if (foundVideo) {
      setSelectedVideo(foundVideo);
      setActiveIndex(relatedVideos.indexOf(foundVideo)); // Update activeIndex based on found video
    }
  }, [language, relatedVideos, selectedVideo]);

  return (
    <>
      <div className="section-spacing inspection-section">
        <h2>{sectionTitle}</h2>
        <div className="row-container">
          <div className="preview-video">
            <div className="custom-card">
              <div className='preview-video-continer'>
                {selectedVideo && (
                  <video
                    controls
                    ref={previewVideoRef}
                    id="previewVideo"
                    key={selectedVideo.video}
                    autoPlay
                    loop
                    muted
                    playsInline
                    poster={getPoster(selectedVideo?.poster)}
                  >
                    <source src={selectedVideo ? `/Assets/videos/${selectedVideo.video}` : ''} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                )}

                {(!isPlaying || !isAutoplaying) && (
                  <div className="overlay" onClick={handlePlayPause}>
                    <div className="custom-play-button">
                      <img src="/Assets/images/play-icon.svg" alt="Play" />
                    </div>
                  </div>
                )}
              </div>
              <p>{selectedVideo ? selectedVideo.description : ''}</p>
              <Link to="https://app-logo-urls.s3.eu-central-1.amazonaws.com/landingPage/Manual+-+app+instructions.pdf" className="download-link">
                {downloadManual}
                <img
                  src='/Assets/images/download-icon.svg'
                  alt=""
                />
              </Link>
            </div>

          </div>
          <div className="related-video">
            <h3>{relatedVideosTitle}</h3>
            {relatedVideos && relatedVideos.map((video, index) => (
              <div
                className={`custom-card ${index === activeIndex ? "active" : ""}`}
                key={index}
                onClick={() => handleVideoClick(video, index)}
              >
                <div className="video-group">
                  <div className="video-wrap">
                    <video
                      poster={getPoster(video.poster)}                      
                      onLoadedMetadata={handleLoadedMetadata}
                    >
                      <source src={video.video} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <div className="video-content">
                    <h4>{video.title}</h4>
                    <p>{video.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Inspection;
